if (!customElements.get('enhanced-media-carousel')) {
  class EnhancedMediaCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-desktop-track]');
      this.tiles = Array.from(this.querySelectorAll('[data-desktop-track] [data-tile]'));
      this.prevBtn = this.querySelector('[data-prev]');
      this.nextBtn = this.querySelector('[data-next]');
      this.expandedTile = null;

      this.tiles.forEach((tile) => {
        tile.addEventListener('click', (event) => this.handleTileClick(event, tile));
      });

      this.prevBtn?.addEventListener('click', () => this.scrollTrack(-1));
      this.nextBtn?.addEventListener('click', () => this.scrollTrack(1));

      this.querySelectorAll('.enhanced-tile-mobile[data-tile]').forEach((tile) => {
        const openModal = () => {
          window.dispatchEvent(new CustomEvent('modal:openById', { detail: tile.dataset.tile }));
        };
        tile.addEventListener('click', openModal);
        tile.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          openModal();
        });
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.collapseAll();
      });

      document.addEventListener('click', (event) => {
        if (!this.contains(event.target)) this.collapseAll();
      });

      this.track?.addEventListener('scroll', () => this.updateArrowState(), { passive: true });

      this.addTrackDragListeners();

      this.measure();
      window.addEventListener('resize', () => {
        this.measure();
        if (this.isDesktop()) {
          this.updateArrowState();
        } else {
          this.prevBtn?.removeAttribute('disabled');
          this.nextBtn?.removeAttribute('disabled');
        }
      });
    }

    addTrackDragListeners() {
      if (!this.track) return;

      const DRAG_THRESHOLD = 5;
      let pointerId = null;
      let startX = 0;
      let startScrollLeft = 0;
      let captured = false;

      this.dragDistance = 0;

      this.track.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        pointerId = event.pointerId;
        startX = event.clientX;
        startScrollLeft = this.track.scrollLeft;
        this.dragDistance = 0;
        captured = false;
      });

      this.track.addEventListener('pointermove', (event) => {
        if (event.pointerId !== pointerId) return;
        const delta = event.clientX - startX;
        this.dragDistance = Math.max(this.dragDistance, Math.abs(delta));
        if (this.dragDistance < DRAG_THRESHOLD) return;

        if (!captured) {
          captured = true;
          this.track.setPointerCapture(pointerId);
          this.track.style.scrollSnapType = 'none';
          this.track.classList.add('is-dragging');
        }

        this.track.scrollLeft = startScrollLeft - delta;
      });

      const endDrag = (event) => {
        if (event.pointerId !== pointerId) return;
        pointerId = null;
        if (!captured) return;
        captured = false;
        this.track.releasePointerCapture?.(event.pointerId);
        this.track.classList.remove('is-dragging');
        this.settleAfterDrag();
      };

      this.track.addEventListener('pointerup', endDrag);
      this.track.addEventListener('pointercancel', endDrag);

      this.track.addEventListener('dragstart', (event) => event.preventDefault());

      this.track.addEventListener('click', (event) => {
        if (this.dragDistance >= DRAG_THRESHOLD) {
          event.preventDefault();
          event.stopPropagation();
        }
        this.dragDistance = 0;
      }, true);
    }

    settleAfterDrag() {
      const restoreSnap = () => {
        this.track.style.removeProperty('scroll-snap-type');
      };

      if (this.expandedTile || !this.tiles.length) {
        restoreSnap();
        return;
      }

      const maxScroll = Math.max(this.track.scrollWidth - this.track.clientWidth, 0);

      if (this.track.scrollLeft >= maxScroll - 1) {
        restoreSnap();
        return;
      }

      const nearest = this.tiles.reduce((closest, tile) =>
        Math.abs(tile.offsetLeft - this.track.scrollLeft) <
        Math.abs(closest.offsetLeft - this.track.scrollLeft) ? tile : closest
      );
      const targetLeft = Math.min(nearest.offsetLeft, maxScroll);

      if (Math.abs(this.track.scrollLeft - targetLeft) < 1) {
        restoreSnap();
        return;
      }

      let settled = false;
      const onSettled = () => {
        if (settled) return;
        settled = true;
        this.track.removeEventListener('scrollend', onSettled);
        clearTimeout(fallback);
        restoreSnap();
      };
      const fallback = setTimeout(onSettled, 500);
      this.track.addEventListener('scrollend', onSettled, { once: true });
      this.track.scrollTo({ left: targetLeft, behavior: 'smooth' });
    }

    // Caches each tile's resting width and locks a fixed row height. Tiles stay in
    // normal flex flow always (expanding one just grows its own width and pushes
    // later siblings along, it never overlays/covers anything), so resting width
    // stays valid until the next resize.
    measure() {
      if (!this.track || !this.tiles.length || this.expandedTile) return;

      this.track.style.removeProperty('--enhanced-tile-height');
      this.tiles.forEach((tile) => {
        tile.style.removeProperty('width');
      });

      this.gap = parseFloat(getComputedStyle(this.track).columnGap) || 16;
      this.tiles.forEach((tile) => {
        tile.restWidth = tile.getBoundingClientRect().width;
      });

      this.track.style.setProperty('--enhanced-tile-height', `${this.tiles[0].getBoundingClientRect().height}px`);
      this.updateArrowState();
    }

    // Desktop-only: the same prev/next buttons also drive the mobile Swiper
    // instance, which manages its own disabled state, so don't touch it there.
    updateArrowState() {
      if (!this.track || !this.isDesktop()) return;
      const maxScroll = this.track.scrollWidth - this.track.clientWidth;
      this.prevBtn?.toggleAttribute('disabled', this.track.scrollLeft <= 1);
      this.nextBtn?.toggleAttribute('disabled', this.track.scrollLeft >= maxScroll - 1);
    }

    isDesktop() {
      return window.matchMedia('(min-width: 768px)').matches;
    }

    handleTileClick(event, tile) {
      if (!this.isDesktop()) return;
      if (event.target.closest('[data-tile-cta]')) return;

      const isExpanded = tile === this.expandedTile;
      if (isExpanded && !event.target.closest('[data-tile-toggle]')) return;

      event.preventDefault();
      this.collapseAll();
      if (!isExpanded) this.expandTile(tile);
    }

    expandTile(tile) {
      if (tile.restWidth === undefined) return;

      const toggle = tile.querySelector('[data-tile-toggle]');
      const cta = tile.querySelector('[data-tile-cta]');

      tile.style.width = `${tile.restWidth}px`;
      tile.classList.add('is-expanded');
      toggle?.setAttribute('aria-expanded', 'true');
      toggle?.setAttribute('aria-label', 'Collapse tile');
      cta?.setAttribute('tabindex', '0');
      this.expandedTile = tile;

      const gap = this.gap || 16;
      const targetWidth = Math.min(tile.restWidth * 3 + gap * 2, this.track.clientWidth);
      const growth = targetWidth - tile.restWidth;

      const grow = () => {
        requestAnimationFrame(() => {
          tile.style.width = `${targetWidth}px`;
        });
      };

      // shift left (as far as the track can go) first, and only start growing once
      // that scroll has fully settled — doing both at once is what reads as jerky.
      // The tile is about to get wider, which grows the track's total scrollable
      // width by that same amount — clamp against that (not the stale pre-growth
      // scrollWidth), or the last tile's expanded portion (and its close button)
      // can end up pushed past the visible/scrollable window entirely.
      const maxScroll = Math.max(this.track.scrollWidth + growth - this.track.clientWidth, 0);
      const targetLeft = Math.min(tile.offsetLeft, maxScroll);

      if (Math.abs(this.track.scrollLeft - targetLeft) < 1) {
        grow();
      } else {
        let settled = false;
        const onSettled = () => {
          if (settled) return;
          settled = true;
          this.track.removeEventListener('scrollend', onSettled);
          clearTimeout(fallback);
          grow();
        };
        const fallback = setTimeout(onSettled, 500);
        this.track.addEventListener('scrollend', onSettled, { once: true });
        this.track.scrollTo({ left: targetLeft, behavior: 'smooth' });
      }

      // fade the copy/CTA in only after the tile has finished growing
      const onGrown = (event) => {
        if (event.target !== tile || event.propertyName !== 'width') return;
        tile.removeEventListener('transitionend', onGrown);
        if (this.expandedTile !== tile) return;

        // the pre-growth scroll above can only reach as far as the row's width
        // *at that moment* (the browser clamps it) — now that the tile has
        // actually grown wider, correct the scroll so the tile (and its close
        // button) can't end up sitting past the visible/scrollable window
        const settledMaxScroll = Math.max(this.track.scrollWidth - this.track.clientWidth, 0);
        const settledTarget = Math.min(tile.offsetLeft, settledMaxScroll);
        if (this.track.scrollLeft < settledTarget) {
          this.track.scrollLeft = settledTarget;
        }

        tile.classList.add('content-visible');
      };
      tile.addEventListener('transitionend', onGrown);
    }

    collapseAll() {
      const tile = this.expandedTile;
      if (!tile) return;

      const toggle = tile.querySelector('[data-tile-toggle]');
      const cta = tile.querySelector('[data-tile-cta]');
      const reveal = tile.querySelector('.enhanced-reveal');
      const wasVisible = tile.classList.contains('content-visible');

      toggle?.setAttribute('aria-expanded', 'false');
      toggle?.setAttribute('aria-label', 'Expand tile');
      cta?.setAttribute('tabindex', '-1');
      tile.classList.remove('content-visible');
      this.expandedTile = null;

      const shrink = () => {
        tile.style.width = `${tile.restWidth}px`;

        const onShrunk = (event) => {
          if (event.propertyName !== 'width') return;
          tile.classList.remove('is-expanded');
          tile.style.removeProperty('width');
          tile.removeEventListener('transitionend', onShrunk);
        };
        tile.addEventListener('transitionend', onShrunk);
      };

      // fade the copy/CTA out first, then shrink the tile back down
      if (wasVisible && reveal) {
        const onFaded = (event) => {
          if (event.target !== reveal || event.propertyName !== 'opacity') return;
          reveal.removeEventListener('transitionend', onFaded);
          shrink();
        };
        reveal.addEventListener('transitionend', onFaded);
      } else {
        shrink();
      }
    }

    scrollTrack(direction) {
      if (!this.track || !this.tiles.length) return;
      const step = (this.tiles[0].restWidth || this.track.clientWidth / 3.5) + (this.gap || 16);
      this.track.scrollBy({ left: direction * step, behavior: 'smooth' });
    }
  }

  customElements.define('enhanced-media-carousel', EnhancedMediaCarousel);
}
